import { useGetSubscription } from "@/app/_hooks/subscription/useGetSubscription";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { Timestamp } from "firebase/firestore";

import React, { useState, createContext, ReactNode, useEffect } from "react";

type SubscriptionContext = Subscription;

interface SubscriptionContextProps {
  subscriptionContext: SubscriptionContext;
  setSubscriptionContext: React.Dispatch<
    React.SetStateAction<SubscriptionContext>
  >;
  // React Query's refetch: resolves once the fresh subscription is in.
  refreshSubscription: () => Promise<unknown>;
  // False until the real subscription has loaded. The context defaults to
  // "master" while loading so most UI renders optimistically; gates that must
  // fail closed (e.g. destructive scan flows) check this first.
  isSubscriptionReady: boolean;
}

const defaultSubscriptionContext: SubscriptionContext = {
  createdAt: Timestamp.now(),
  smsCount: 0,
  smsLimit: 100,
  level: "master",
  active: true,
} as Subscription;

export const SubscriptionContext = createContext<SubscriptionContextProps>({
  subscriptionContext: defaultSubscriptionContext,
  setSubscriptionContext: () => {},
  refreshSubscription: async () => {},
  isSubscriptionReady: false,
});

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider = ({
  children,
}: SubscriptionProviderProps) => {
  const user = useUserContext();
  const { data: subscription, refetch } = useGetSubscription(user?.uid);
  const [subscriptionContext, setSubscriptionContext] =
    useState<SubscriptionContext>(defaultSubscriptionContext);
  const [isSubscriptionReady, setIsSubscriptionReady] = useState(false);

  useEffect(() => {
    if (user?.uid && subscription) {
      setSubscriptionContext(subscription);
      setIsSubscriptionReady(true);
    }
  }, [subscription, user?.uid]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptionContext,
        setSubscriptionContext,
        refreshSubscription: refetch,
        isSubscriptionReady,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
